import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import {
  CombinedScenarioChart,
  SequestrationScenarioChart,
  SequestrationScenarioGauges,
} from "../charts/charts";
import "./SectionThree.css";
import CollapsibleCitation from "../tools/CollapsibleCitation";
import InformationPopUp from "../tools/InformationPopUp";
import { Typography } from "@mui/material";
import CarbonBalance from "./CarbonBalance";

export default function Escenarios() {
  const mapContainerClass = "map-container";
  const sectionRef = useRef(null);
    const isInView = useInView(sectionRef, { amount: 0.5, once: false });
  
    // helper for staggering
    const animateProps = (delay = 0) => ({
      initial: { opacity: 0, y: 10 },
      animate: isInView ? { opacity: 1, y: 0 } : {},
      transition: { duration: 0.8, delay }
    });
  

  return (
    <section
      ref={sectionRef}
      className="h-screen snap-start bg-gradient-to-b from-[#324130] to-[#92843B] text-white"
    >
      <div className="h-full p-6" style={{ marginTop: "20vh" }}>
        <motion.h2
          initial={{ opacity: 0, y: -20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-3xl font-bold mb-[-50] mt-10 text-center"
        >
          Carbon emissions
        </motion.h2>

        {/* Two-column layout */}
        <div className="w-full grid grid-cols-1 lg:grid-cols-2" style={{ marginTop: "5vh" }}>
          
          {/* LEFT column: two line charts */}
          <div className="flex flex-col h-screen p-6" >
            {/* Centered heading over both columns */}
            <div className="absolute left-0 right-0 top-0 flex justify-center items-center" style={{ marginTop: "-8vh", zIndex: 10 }}>
              <motion.h2
                initial={{ opacity: 0, y: -20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8 }}
                className="text-xl font-bold"
                style={{ background: "rgba(0,0,0,0.25)", borderRadius: "0.5rem", padding: "0.75rem 2rem" }}
              >
                Carbon emissions from urbanization
              </motion.h2>
            </div>

            <motion.div {...animateProps(0.2)}>
              <motion.h3
                initial={{ opacity: 0, y: -10 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="text-xl font-semibold mb-2"
              >
                Change in forest and settlement area by year
                <InformationPopUp
                  title="Change in forest and settlement area by year"
                  description="This chart shows the historical change in area covered by forests (left) and settlements (right) up to the last available year (2022). Additionally, it includes predictions 20 years into the future, corresponding to three different urbanization scenarios:
            •	Inertial (black): The urbanization rate follows the same pace as historical observations.
            •	Accelerated (red): Urbanization increases by 15%.
            •	Decelerated (green): Urbanization decreases by 15%."
                  bg_color="linear-gradient(to bottom, rgba(50,65,48,0.8), rgba(146,132,59,1) 45%)"
                  title_citation="Global GLC_FCS30D: Global Land Cover"
                  source="Own elaboration based on SISEPUEDE"
                  fullCitation="https://sisepuede.readthedocs.io/"
                />
              </motion.h3>
              <div className="bg-black bg-opacity-30 rounded-md p-4 flex flex-row gap-4 items-start"style={{ height: "35vh", maxHeight: "35vh" }}>
                
                <CombinedScenarioChart />
              </div>
            </motion.div>
            <motion.div {...animateProps(0.6)}>
              <motion.h3
                initial={{ opacity: 0, y: -10 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 1.0 }}
                className="text-xl font-semibold mb-2"
              >
                Carbon Balance
              <InformationPopUp title="Carbon Balance"
                description="This chart shows the carbon balance due to two mechanisms: land use transitions and carbon captured by forests. A blue pixel indicates that the net was negative (i.e., carbon was absorbed), while a red pixel means it was emitted. The quantities are total accumulations from 2000 to 2022."
                bg_color="linear-gradient(to bottom, rgba(50,65,48,0.8), rgba(146,132,59,1) 45%)"
                title_citation="GLC_FCS30D: Global Land Cover"
                source="Own elaboration based on SISEPUEDE"
                fullCitation="https://sisepuede.readthedocs.io/"
              />
              </motion.h3>
              <div className={`${mapContainerClass} relative w-full h-[40%] rounded-xl shadow-lg mt-5 `} style={{ height: "35vh", maxHeight: "35vh" }}>
                 <CarbonBalance />
              </div>
              
              
            </motion.div>
          </div>
          {/* New RIGHT column: emissions stacked bar chart */}

          <div className="flex flex-col h-screen p-6" >
            {/* Carbon sequestration by scenarios */}
            <motion.div {...animateProps(1.0)}>
              <motion.h3
                initial={{ opacity: 0, y: -20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 1.0 }}
                className="text-xl font-semibold mb-2"
              >
              Carbon capture potential by scenarios
              <InformationPopUp title="Carbon capture potential by scenarios"
                description="This chart shows the carbon captured each year, as well as the potential for future capture for the three urbanization scenarios. Additionally, it includes the difference between the decelerated and accelerated scenarios, which represents the carbon capture potential in a context without conservation and reforestation measures compared to one in which such actions are implemented."
                bg_color="linear-gradient(to bottom, rgba(50,65,48,0.8), rgba(146,132,59,1) 45%)"
                title_citation="GLC_FCS30D: Global Land Cover"
                source="Own elaboration based on SISEPUEDE"
                fullCitation="https://sisepuede.readthedocs.io/"
              />
              </motion.h3>
              <div
                className="bg-black bg-opacity-30 rounded-md p-4 flex flex-row gap-4 items-start"
                style={{ height: "35vh", maxHeight: "35vh" }}
              >
               
                  <SequestrationScenarioChart />
                
              </div>
            </motion.div>

            <motion.div {...animateProps(1.0)}>
              <motion.h3
                initial={{ opacity: 0, y: -10 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 1.0 }}
                className="text-xl font-semibold mb-2"
              >
               Carbon capture potential by scenarios
              <InformationPopUp title="Carbon capture potential by scenarios"
                description="This chart shows the carbon captured each year, as well as the potential for future capture for the three urbanization scenarios. Additionally, it includes the difference between the decelerated and accelerated scenarios, which represents the carbon capture potential in a context without conservation and reforestation measures compared to one in which such actions are implemented."
                bg_color="linear-gradient(to bottom, rgba(50,65,48,0.8), rgba(146,132,59,1) 45%)"
                title_citation="GLC_FCS30D: Global Land Cover"
                source="Own elaboration based on SISEPUEDE"
                fullCitation="https://sisepuede.readthedocs.io/"
              />
              </motion.h3>
              <div className={` bg-black bg-opacity-30  rounded-md relative rounded-xl shadow-lg mt-5 `} style={{ height: "35vh", maxHeight: "35vh" }}>
                 <div style={{ flex: 1.2, height: "100%", overflow: "hidden" }}>
                  <SequestrationScenarioGauges />
                </div>
              </div>
            </motion.div>

             </div>
             
        </div>
      </div>
    </section>
  );
}